import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { apiGet, apiPost, apiPut, apiDelete } from "@/lib/api";
import { toast } from "sonner";
import {
  Wand2,
  Save,
  Sparkles,
  Trash2,
  Plus,
  RefreshCw,
  FileText,
  Mic,
  Map as MapIcon,
  Library,
} from "lucide-react";

import { OWLBERT_DARK } from "@/lib/owlbert";
const OWL_PORTRAIT = OWLBERT_DARK;

const TAGS = ["fact", "preference", "performance", "learning", "voice", "pipeline", "feedback"];

// ---- tiny markdown renderer (headings, bullets, blockquotes, code, hr, links) ----
function renderMarkdown(md = "") {
  const lines = md.split("\n");
  const out = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (/^---\s*$/.test(line)) {
      out.push(<hr key={`hr-${i}`} />);
      i++;
      continue;
    }
    if (/^# /.test(line)) {
      out.push(<h1 key={`h1-${i}`}>{inline(line.slice(2))}</h1>);
      i++;
      continue;
    }
    if (/^## /.test(line)) {
      out.push(<h2 key={`h2-${i}`}>{inline(line.slice(3))}</h2>);
      i++;
      continue;
    }
    if (/^### /.test(line)) {
      out.push(<h3 key={`h3-${i}`}>{inline(line.slice(4))}</h3>);
      i++;
      continue;
    }
    if (/^> /.test(line)) {
      // collect consecutive blockquote lines
      const buf = [];
      while (i < lines.length && /^> /.test(lines[i])) {
        buf.push(lines[i].slice(2));
        i++;
      }
      out.push(<blockquote key={`q-${i}`}>{inline(buf.join(" "))}</blockquote>);
      continue;
    }
    if (/^[-*] /.test(line)) {
      const buf = [];
      while (i < lines.length && /^[-*] /.test(lines[i])) {
        buf.push(lines[i].replace(/^[-*] /, ""));
        i++;
      }
      out.push(
        <ul key={`ul-${i}`}>
          {buf.map((b, idx) => (
            <li key={idx}>{inline(b)}</li>
          ))}
        </ul>
      );
      continue;
    }
    if (line.trim() === "") {
      i++;
      continue;
    }
    // paragraph — accumulate consecutive non-empty, non-special lines
    const buf = [line];
    i++;
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      !/^(#{1,3} |> |[-*] |---)/.test(lines[i])
    ) {
      buf.push(lines[i]);
      i++;
    }
    out.push(<p key={`p-${i}`}>{inline(buf.join(" "))}</p>);
  }
  return out;
}

function inline(text) {
  // bold **x** -> <strong>, code `x` -> <code>, italic _x_ -> <em>
  const parts = [];
  let rest = text;
  const re = /(\*\*[^*]+\*\*|`[^`]+`|_[^_]+_)/g;
  let last = 0;
  let m;
  let key = 0;
  while ((m = re.exec(rest)) !== null) {
    if (m.index > last) parts.push(rest.slice(last, m.index));
    const token = m[0];
    if (token.startsWith("**")) parts.push(<strong key={key++}>{token.slice(2, -2)}</strong>);
    else if (token.startsWith("`")) parts.push(<code key={key++}>{token.slice(1, -1)}</code>);
    else parts.push(<em key={key++}>{token.slice(1, -1)}</em>);
    last = m.index + token.length;
  }
  if (last < rest.length) parts.push(rest.slice(last));
  return parts;
}

// ---------------------------------------------------------------------------
// Document tab — CLIENT / VOICE / STRATEGY
// ---------------------------------------------------------------------------
function DocumentTab({ workspaceId, kind, label, generator, generatorLabel, helpText }) {
  const [doc, setDoc] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    try {
      const r = await apiGet(`/workspaces/${workspaceId}/brain/${kind}`);
      setDoc(r);
      setDraft(r?.content || "");
    } catch (e) {
      toast.error("could not load " + kind);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [workspaceId, kind]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await apiPut(`/workspaces/${workspaceId}/brain/${kind}`, { content: draft });
      setDoc(r);
      setEditing(false);
      toast.success(`${kind}.md saved · v${r.version}`);
    } catch (e) {
      toast.error("save failed");
    } finally {
      setBusy(false);
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await generator();
      setDoc(r);
      setDraft(r?.content || "");
      toast.success(`${kind}.md generated · v${r.version}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const hasContent = (doc?.content || "").trim().length > 0;

  return (
    <div className="space-y-6" data-testid={`brain-tab-${kind}`}>
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline">{kind}.md</div>
          <h2 className="serif text-3xl text-parchment mt-1">{label}</h2>
          {helpText && (
            <p className="text-sm text-stone mt-2 max-w-xl leading-relaxed">{helpText}</p>
          )}
          {hasContent && (
            <div className="mono text-[11px] text-stone-dim mt-2">
              version {doc.version} · updated {doc.updated_at?.slice(0, 10)} by {doc.updated_by}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={generate}
            disabled={generating}
            data-testid={`brain-${kind}-generate`}
            className="btn-ghost inline-flex items-center gap-2"
          >
            <Wand2 className="w-3.5 h-3.5" />
            {generating ? "Working…" : generatorLabel}
          </button>
          {editing ? (
            <>
              <button
                onClick={() => {
                  setDraft(doc?.content || "");
                  setEditing(false);
                }}
                className="btn-ghost"
                data-testid={`brain-${kind}-cancel`}
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={busy}
                data-testid={`brain-${kind}-save`}
                className="btn-primary inline-flex items-center gap-2"
              >
                <Save className="w-3.5 h-3.5" />
                {busy ? "Saving…" : "Save"}
              </button>
            </>
          ) : (
            <button
              onClick={() => setEditing(true)}
              className="btn-ghost"
              data-testid={`brain-${kind}-edit`}
            >
              Edit
            </button>
          )}
        </div>
      </div>

      {editing ? (
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          data-testid={`brain-${kind}-textarea`}
          className="w-full min-h-[640px] markdown-surface font-mono text-[13px] outline-none resize-y"
          style={{ fontFamily: '"JetBrains Mono", monospace', lineHeight: 1.7 }}
          spellCheck={false}
        />
      ) : hasContent ? (
        <article className="markdown-surface fade-up" data-testid={`brain-${kind}-rendered`}>
          {renderMarkdown(doc.content)}
        </article>
      ) : (
        <div className="hairline px-10 py-16 text-center" data-testid={`brain-${kind}-empty`}>
          <FileText className="w-8 h-8 text-stone mx-auto mb-4" strokeWidth={1} />
          <div className="serif text-2xl text-parchment mb-2">Nothing written yet</div>
          <p className="text-sm text-stone max-w-md mx-auto">
            {kind === "client" &&
              "Auto-draft this from your workspace data, or write it by hand. It's the record of who this client is."}
            {kind === "voice" &&
              "Run extraction on uploaded writing samples to capture their sentence patterns and vocabulary."}
            {kind === "strategy" &&
              "Fill in the client profile and voice guide first, then draft the content plan from them."}
          </p>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Memory tab
// ---------------------------------------------------------------------------
function MemoryTab({ workspaceId, counts }) {
  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [newTag, setNewTag] = useState("fact");
  const [newContent, setNewContent] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter) params.tag = filter;
      if (q) params.q = q;
      const r = await apiGet(`/workspaces/${workspaceId}/brain/memories`, params);
      setMemories(r);
    } catch (e) {
      toast.error("could not load memories");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [workspaceId, filter]);

  const submit = async () => {
    if (!newContent.trim()) return;
    try {
      await apiPost(`/workspaces/${workspaceId}/brain/memories`, {
        tag: newTag,
        content: newContent.trim(),
        source: "manual",
      });
      setNewContent("");
      setNewOpen(false);
      toast.success("memory saved");
      load();
    } catch (e) {
      toast.error("save failed");
    }
  };

  const del = async (id) => {
    try {
      await apiDelete(`/workspaces/${workspaceId}/brain/memories/${id}`);
      setMemories(memories.filter((m) => m.memory_id !== id));
      toast.success("memory deleted");
    } catch (e) {
      toast.error("delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="brain-tab-memory">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline">layer 2</div>
          <h2 className="serif text-3xl text-parchment mt-1">Long-term Memory</h2>
          <p className="text-sm text-stone mt-2 max-w-xl">
            tagged, searchable. the librarian saves what matters; the rest fades.
          </p>
        </div>
        <button
          onClick={() => setNewOpen((v) => !v)}
          data-testid="memory-new-btn"
          className="btn-primary inline-flex items-center gap-2"
        >
          <Plus className="w-3.5 h-3.5" />
          new memory
        </button>
      </div>

      {/* Tag filter strip */}
      <div className="hairline px-6 py-4 flex items-center gap-3 flex-wrap">
        <button
          onClick={() => setFilter("")}
          className={`mono text-[11px] uppercase tracking-wider px-2 py-1 border transition-colors ${
            filter === ""
              ? "border-terracotta text-terracotta"
              : "border-hairline text-stone hover:text-parchment"
          }`}
          data-testid="memory-filter-all"
        >
          all · {Object.values(counts || {}).reduce((a, b) => a + b, 0)}
        </button>
        {TAGS.map((t) => (
          <button
            key={t}
            onClick={() => setFilter(t)}
            data-testid={`memory-filter-${t}`}
            className={`mono text-[11px] uppercase tracking-wider px-2 py-1 border transition-colors ${
              filter === t
                ? "border-terracotta text-terracotta"
                : "border-hairline text-stone hover:text-parchment"
            }`}
          >
            {t} · {counts?.[t] ?? 0}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <input
            type="text"
            placeholder="search…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            data-testid="memory-search"
            className="input-block max-w-[220px]"
          />
        </div>
      </div>

      {/* New entry form */}
      {newOpen && (
        <div className="hairline px-6 py-5 space-y-3" data-testid="memory-new-form">
          <div className="flex gap-2 flex-wrap">
            {TAGS.map((t) => (
              <button
                key={t}
                onClick={() => setNewTag(t)}
                className={`tag-chip ${newTag === t ? "" : "opacity-50"}`}
                data-tag={t}
                data-testid={`memory-new-tag-${t}`}
                style={newTag === t ? { background: "hsl(var(--accent) / 0.15)" } : undefined}
              >
                {t}
              </button>
            ))}
          </div>
          <textarea
            value={newContent}
            onChange={(e) => setNewContent(e.target.value)}
            placeholder="one-line distilled fact, preference, learning, or pipeline event…"
            rows={3}
            data-testid="memory-new-content"
            className="input-block resize-y"
          />
          <div className="flex justify-end gap-2">
            <button onClick={() => setNewOpen(false)} className="btn-ghost">
              cancel
            </button>
            <button
              onClick={submit}
              disabled={!newContent.trim()}
              className="btn-primary"
              data-testid="memory-new-submit"
            >
              save
            </button>
          </div>
        </div>
      )}

      {/* Timeline */}
      {loading ? (
        <div className="overline py-10">reading the archive…</div>
      ) : memories.length === 0 ? (
        <div className="hairline px-10 py-16 text-center">
          <Library className="w-8 h-8 text-stone mx-auto mb-4" strokeWidth={1} />
          <div className="serif text-2xl text-parchment mb-2">empty</div>
          <p className="text-sm text-stone max-w-md mx-auto">
            nothing to remember yet for this filter. start adding facts, preferences, learnings as
            they happen.
          </p>
        </div>
      ) : (
        <div className="timeline-stem space-y-5 fade-up" data-testid="memory-timeline">
          {memories.map((m) => (
            <div key={m.memory_id} className="timeline-node" data-testid={`memory-${m.memory_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="tag-chip" data-tag={m.tag}>
                      {m.tag}
                    </span>
                    <span className="mono text-[10px] text-stone-dim uppercase tracking-wider">
                      {m.created_at?.slice(0, 10)} · {m.source || "manual"}
                    </span>
                  </div>
                  <div className="text-[15px] text-parchment leading-relaxed">{m.content}</div>
                </div>
                <button
                  onClick={() => del(m.memory_id)}
                  className="text-stone-dim hover:text-terracotta transition-colors shrink-0 p-1"
                  data-testid={`memory-delete-${m.memory_id}`}
                  title="delete"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Brain page
// ---------------------------------------------------------------------------
export default function Brain() {
  const { workspaceId } = useParams();
  const [tab, setTab] = useState("client");
  const [bundle, setBundle] = useState(null);

  const loadBundle = async () => {
    try {
      const r = await apiGet(`/workspaces/${workspaceId}/brain`);
      setBundle(r);
    } catch (e) {
      toast.error("could not load brain");
    }
  };

  useEffect(() => {
    loadBundle();
    // eslint-disable-next-line
  }, [workspaceId]);

  const tabs = [
    { k: "client", label: "Client profile", icon: FileText },
    { k: "voice", label: "Voice guide", icon: Mic },
    { k: "strategy", label: "Strategy", icon: MapIcon },
    { k: "product", label: "Product", icon: Sparkles },
    { k: "memory", label: "Saved memories", icon: Library },
  ];

  return (
    <Layout
      title="Client Memory"
      actions={
        <div className="hidden md:flex items-center gap-3">
          <img
            src={OWL_PORTRAIT}
            alt="Owlbert"
            className="owlbert-portrait w-16 h-16 object-cover"
          />
        </div>
      }
    >
      {/* Tab nav */}
      <div className="flex items-end border-b border-hairline mb-10 -mx-1 px-1">
        {tabs.map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`brain-tab-btn-${t.k}`}
            className={`tab ${tab === t.k ? "active" : ""} inline-flex items-center gap-2`}
          >
            <t.icon className="w-3.5 h-3.5" strokeWidth={1.5} />
            {t.label}
          </button>
        ))}
      </div>

      {tab === "client" && (
        <DocumentTab
          workspaceId={workspaceId}
          kind="client"
          label="Who they are"
          generator={() =>
            apiPost(`/workspaces/${workspaceId}/brain/client/generate`, { extra_notes: "" })
          }
          generatorLabel="Auto-draft from workspace data"
          helpText="The reference document for who this client is: the person, the business, the offer, and the target customer."
        />
      )}
      {tab === "voice" && (
        <DocumentTab
          workspaceId={workspaceId}
          kind="voice"
          label="How they write"
          generator={() =>
            apiPost(`/workspaces/${workspaceId}/brain/voice/extract`, {})
          }
          generatorLabel="Extract from writing samples"
          helpText="How this client writes: sentence patterns, opening moves, vocabulary they use and avoid, and beliefs they repeat."
        />
      )}
      {tab === "strategy" && (
        <DocumentTab
          workspaceId={workspaceId}
          kind="strategy"
          label="The plan"
          generator={() =>
            apiPost(`/workspaces/${workspaceId}/brain/strategy/generate`, {
              use_top_posts: true,
            })
          }
          generatorLabel="Draft from profile + voice"
          helpText="The content plan: pillars, post-type rotation, CTAs, and lead magnets. Needs the client profile and voice guide first."
        />
      )}
      {tab === "product" && (
        <DocumentTab
          workspaceId={workspaceId}
          kind="product"
          label="Their product"
          helpText="Everything the AI needs to know about this client's product / service to reference it naturally in comments — positioning, signals that indicate a real problem it solves, categories NOT to mention in (grief, layoffs, injuries), and any customer proof the AI is authorized to cite. Leave empty if this client has no product to reference."
        />
      )}
      {tab === "memory" && (
        <MemoryTab workspaceId={workspaceId} counts={bundle?.memory_counts} />
      )}
    </Layout>
  );
}
